import { useEffect, useMemo, useState } from 'react';
import type { StoryboardSegment } from '../../types';

const INITIAL_RENDER_COUNT = 12;
const BATCH_SIZE = 12;
const BATCH_DELAY_MS = 32;

export const useSegmentRenderQueue = (
  segments: StoryboardSegment[],
  requiredSegmentIds: string[] = []
): StoryboardSegment[] => {
  const [renderCount, setRenderCount] = useState<number>(() =>
    Math.min(segments.length, INITIAL_RENDER_COUNT)
  );

  useEffect(() => {
    setRenderCount((current) => Math.min(Math.max(current, INITIAL_RENDER_COUNT), segments.length));
  }, [segments.length]);

  useEffect(() => {
    if (renderCount >= segments.length) {
      return;
    }
    const timer = setTimeout(() => {
      setRenderCount((current) => Math.min(segments.length, current + BATCH_SIZE));
    }, BATCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [renderCount, segments.length]);

  return useMemo(() => {
    if (renderCount >= segments.length && requiredSegmentIds.length === 0) {
      return segments;
    }
    const requiredSet = new Set(requiredSegmentIds);
    const result: StoryboardSegment[] = [];
    segments.forEach((segment, index) => {
      if (index < renderCount || requiredSet.has(segment.segmentId)) {
        result.push(segment);
      }
    });
    return result;
  }, [renderCount, requiredSegmentIds, segments]);
};
